import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  IconButton,
  useTheme,
  alpha,
  SelectChangeEvent
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

interface Props {
  loadedPipelines: any[];
  activePipeline: any | null;
  viewOnly: boolean;
  onPipelineChange: (event: SelectChangeEvent<string>) => void;
  onEditClick: () => void;
  actionButtons?: React.ReactNode;
}

const PipelineInfoPanel = ({
  loadedPipelines,
  activePipeline,
  viewOnly,
  onPipelineChange,
  onEditClick,
  actionButtons
}: Props) => {
  const theme = useTheme();

  if (!activePipeline && loadedPipelines.length === 0) {
    return null;
  }

  const pipelineName = activePipeline?.name || 'Untitled Pipeline';
  const pipelineDescription = activePipeline?.description || '';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 10,
        boxShadow: 2,
        borderRadius: '10px',
        p: 1.5,
        maxWidth: { xs: 'calc(100% - 24px)', sm: 320 },
        width: 'calc(100% - 24px)',
        border: `1px solid ${theme.palette.divider}`,
        backdropFilter: 'blur(10px)',
        backgroundColor: alpha(theme.palette.background.paper, 0.85),
      }}
    >
      {loadedPipelines.length > 1 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
            <InputLabel id="pipeline-select-label">Active Pipeline</InputLabel>
            <Select
              labelId="pipeline-select-label"
              id="pipeline-select"
              value={activePipeline?.id || ''}
              label="Active Pipeline"
              onChange={onPipelineChange}
              sx={{
                borderRadius: '8px',
                fontSize: '0.875rem',
                '.MuiSelect-select': {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {loadedPipelines.map((p) => (
                <MenuItem
                  key={p.id}
                  value={p.id}
                  sx={{
                    fontSize: '0.875rem',
                    maxWidth: 280,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.name || 'Untitled Pipeline'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!viewOnly && (
            <Tooltip title="Edit pipeline info">
              <IconButton size="small" onClick={onEditClick} sx={{ flexShrink: 0 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
          <Tooltip title={pipelineName || 'Untitled Pipeline'} placement="top-start">
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                fontSize: '0.9rem',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                flexGrow: 1,
                cursor: 'default',
                minWidth: 0,
              }}
            >
              {pipelineName || 'Untitled Pipeline'}
            </Typography>
          </Tooltip>
          {!viewOnly && (
            <Tooltip title="Edit pipeline info">
              <IconButton size="small" onClick={onEditClick} sx={{ flexShrink: 0 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {pipelineDescription && (
        <Tooltip title={pipelineDescription} placement="bottom-start">
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mt: 0.5,
              lineHeight: 1.2,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              cursor: 'default',
            }}
          >
            {pipelineDescription}
          </Typography>
        </Tooltip>
      )}
      {actionButtons && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${theme.palette.divider}`, pt: 1 }}>
          {actionButtons}
        </Box>
      )}
    </Box>
  );
};

export default PipelineInfoPanel;
